import { events } from "@planzone/events";
import { Boundaries } from "@planzone/core";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = {
  request: {
    isPending: false,
    isSuccessful: false,
    isFailure: false,
  },
  projectExpenses: [],
};

function handleRequest(state: State): State {
  return { ...state, request: setRequestStateToPending() };
}

function handleSuccess(
  state: State,
  projectExpenses: Boundaries["retrieveProjectExpenses"]["success"],
): State {
  return { projectExpenses, request: setRequestStateToSuccess() };
}

function handleFailure(state: State): State {
  return { ...state, request: setRequestStateToFailure() };
}

function handleCreateProjectExpenseRequest(
  state: State,
  { projectExpense }: Boundaries["createProjectExpense"]["request"],
): State {
  return {
    ...state,
    projectExpenses: [
      { ...projectExpense, uuid: projectExpense.name },
      ...state.projectExpenses,
    ],
  };
}

function handleCreateProjectExpenseSuccess(
  state: State,
  { projectExpense }: Boundaries["createProjectExpense"]["success"],
): State {
  return {
    ...state,
    projectExpenses: [
      projectExpense,
      ...state.projectExpenses.filter(
        ({ uuid }) => uuid !== projectExpense.name,
      ),
    ],
  };
}

function handleCreateProjectExpenseFailure(
  state: State,
  { projectExpense }: Boundaries["createProjectExpense"]["failure"],
): State {
  return {
    ...state,
    projectExpenses: state.projectExpenses.filter(
      ({ uuid }) => uuid !== projectExpense.name,
    ),
  };
}

function handleRemoveProjectExpenseRequest(
  state: State,
  projectExpense: Boundaries["removeProjectExpense"]["request"],
): State {
  return {
    ...state,
    projectExpenses: state.projectExpenses.filter(
      ({ uuid }) => uuid !== projectExpense.uuid,
    ),
  };
}

function handleRemoveProjectExpenseSuccess(
  state: State,
  projectExpense: Boundaries["removeProjectExpense"]["success"],
): State {
  return {
    ...state,
    projectExpenses: state.projectExpenses.filter(
      ({ uuid }) => uuid !== projectExpense.uuid,
    ),
  };
}

function handleRemoveProjectExpenseFailure(
  state: State,
  projectExpense: Boundaries["removeProjectExpense"]["failure"],
): State {
  return {
    ...state,
    projectExpenses: [projectExpense, ...state.projectExpenses],
  };
}

function handleEditProjectExpenseRequest(
  state: State,
  {
    projectExpense,
    nextProjectExpense,
  }: Boundaries["editProjectExpense"]["request"],
): State {
  return {
    ...state,
    projectExpenses: state.projectExpenses.map((p) =>
      p.uuid === projectExpense.uuid
        ? { ...projectExpense, ...nextProjectExpense }
        : p,
    ),
  };
}

function handleEditProjectExpenseFailure(
  state: State,
  { projectExpense }: Boundaries["editProjectExpense"]["failure"],
): State {
  return {
    ...state,
    projectExpenses: state.projectExpenses.map((p) =>
      p.uuid === projectExpense.uuid ? projectExpense : p,
    ),
  };
}

function handleEditProjectExpenseSuccess(
  state: State,
  { projectExpense }: Boundaries["editProjectExpense"]["success"],
): State {
  return {
    ...state,
    projectExpenses: state.projectExpenses.map((p) =>
      p.uuid === projectExpense.uuid
        ? {
            ...p,
            ...projectExpense,
            attachmentsCount:
              projectExpense.attachments?.length ?? p.attachments.length,
          }
        : p,
    ),
  };
}

function handleAttachDocumentsToElementSuccess(
  state: State,
  { elementId, documents }: Boundaries["attachDocumentsToElement"]["success"],
): State {
  const index = state.projectExpenses.findIndex((p) => p.uuid === elementId);

  return {
    ...state,
    projectExpenses:
      index !== -1
        ? [
            ...state.projectExpenses.slice(0, index),
            {
              ...state.projectExpenses[index],
              attachments: [
                ...(state.projectExpenses[index].attachments || []),
                ...documents,
              ],
            },
            ...state.projectExpenses.slice(index + 1),
          ]
        : state.projectExpenses,
  };
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrieveProjectExpensesRequested]: handleRequest,
    [events.retrieveProjectExpensesSucceeded]: handleSuccess,
    [events.retrieveProjectExpensesFailed]: handleFailure,
    [events.createProjectExpenseRequested]: handleCreateProjectExpenseRequest,
    [events.createProjectExpenseSucceeded]: handleCreateProjectExpenseSuccess,
    [events.createProjectExpenseFailed]: handleCreateProjectExpenseFailure,
    [events.removeProjectExpenseRequested]: handleRemoveProjectExpenseRequest,
    [events.removeProjectExpenseSucceeded]: handleRemoveProjectExpenseSuccess,
    [events.removeProjectExpenseFailed]: handleRemoveProjectExpenseFailure,
    [events.editProjectExpenseRequested]: handleEditProjectExpenseRequest,
    [events.editProjectExpenseSucceeded]: handleEditProjectExpenseSuccess,
    [events.editProjectExpenseFailed]: handleEditProjectExpenseFailure,
    [events.attachDocumentsToElementSucceeded]:
      handleAttachDocumentsToElementSuccess,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
